import React from "react";

// Public profile card, served only at /AnthonyCulligan (see App.tsx). It is
// deliberately outside the Clerk gates and linked from no menu. Dummy contact
// data for now — replace PROFILE values when the real details are decided.

const PROFILE = {
  name: "Anthony Culligan",
  title: "Founder · kurnal.ai",
  location: "London, UK",
  phone: "+44 7700 900123",
  email: "anthony@example.com",
  web: "https://www.kurnal.ai",
  x: "anthonyculligan",
  linkedin: "anthony-culligan",
  reddit: "anthonyculligan",
};

const icon = {
  phone: (
    <path d="M6.6 10.8a15.5 15.5 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.58 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.4 11.4 0 0 0 .58 3.6 1 1 0 0 1-.25 1z" />
  ),
  email: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" fill="none" strokeWidth="2" stroke="currentColor" />
      <path d="m3 7 9 6 9-6" fill="none" strokeWidth="2" stroke="currentColor" />
    </>
  ),
  web: (
    <>
      <circle cx="12" cy="12" r="9" fill="none" strokeWidth="2" stroke="currentColor" />
      <path d="M3 12h18M12 3a13.5 13.5 0 0 1 0 18M12 3a13.5 13.5 0 0 0 0 18" fill="none" strokeWidth="2" stroke="currentColor" />
    </>
  ),
  x: (
    <path d="M17.5 3h3l-7.1 8.1L21.7 21h-6.4l-4.6-6-5.2 6h-3l7.5-8.6L2.5 3h6.6l4.1 5.5zm-1.1 16h1.7L7.1 4.7H5.3z" />
  ),
  linkedin: (
    <path d="M4.98 3.5A2.5 2.5 0 1 1 5 8.5a2.5 2.5 0 0 1-.02-5zM3 9.5h4V21H3zM9.5 9.5h3.8v1.6h.05a4.2 4.2 0 0 1 3.75-2.06c4 0 4.75 2.64 4.75 6.07V21h-4v-5.2c0-1.24-.02-2.84-1.73-2.84-1.73 0-2 1.35-2 2.75V21h-3.87z" />
  ),
  reddit: (
    <path d="M22 12.1c0-1.2-1-2.2-2.2-2.2-.6 0-1.1.2-1.5.6-1.4-1-3.4-1.6-5.5-1.7l1.1-3.6 3.1.7a1.7 1.7 0 1 0 .2-1l-3.6-.8a.5.5 0 0 0-.6.3l-1.3 4.4c-2.2.1-4.2.7-5.7 1.7-.4-.4-.9-.6-1.5-.6A2.2 2.2 0 0 0 3.3 14c0 .3.1.6.2.8-.1.3-.1.7-.1 1 0 3 3.8 5.4 8.6 5.4s8.6-2.4 8.6-5.4c0-.3 0-.7-.1-1 .3-.4.5-.9.5-1.7zM8.1 14.6a1.4 1.4 0 1 1 2.8 0 1.4 1.4 0 0 1-2.8 0zm7.6 3.9c-1 1-2.8 1-3.7 1s-2.7 0-3.7-1a.4.4 0 0 1 .6-.6c.6.6 1.9.8 3.1.8s2.5-.2 3.1-.8a.4.4 0 0 1 .6.6zm-.2-2.5a1.4 1.4 0 1 1 0-2.8 1.4 1.4 0 0 1 0 2.8z" />
  ),
};

interface RowProps {
  glyph: React.ReactNode;
  label: string;
  value: string;
  href: string;
}

const ContactRow: React.FC<RowProps> = ({ glyph, label, value, href }) => (
  <a
    href={href}
    target={href.startsWith("http") ? "_blank" : undefined}
    rel="noreferrer"
    className="flex items-center gap-4 px-4 py-3.5 rounded-xl bg-white border border-krule hover:border-ksky active:bg-paper transition-colors"
  >
    <span className="flex-none w-10 h-10 rounded-lg bg-paper text-inklight flex items-center justify-center">
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
        {glyph}
      </svg>
    </span>
    <span className="min-w-0">
      <span className="block text-[11px] uppercase tracking-wider text-kslate font-mono">{label}</span>
      <span className="block text-sm text-ink truncate">{value}</span>
    </span>
  </a>
);

const ProfilePage: React.FC = () => (
  <div className="min-h-screen bg-paper text-ink font-sans flex flex-col items-center px-4 py-10">
    <div className="w-full max-w-sm">
      {/* Header card */}
      <div className="rounded-2xl bg-kterm text-ktermtext px-6 pt-8 pb-6 text-center shadow-lg">
        <div className="mx-auto w-24 h-24 rounded-full bg-gradient-to-br from-ksky to-kgreen p-[3px]">
          <div className="w-full h-full rounded-full bg-kterm flex items-center justify-center">
            <span className="text-3xl font-mono text-paper">AC</span>
          </div>
        </div>
        <h1 className="mt-4 text-xl font-semibold text-paper">{PROFILE.name}</h1>
        <p className="mt-1 text-sm text-ktermdim font-mono">{PROFILE.title}</p>
        <p className="mt-0.5 text-xs text-ktermdim">{PROFILE.location}</p>
      </div>

      {/* Contact rows */}
      <div className="mt-5 flex flex-col gap-2.5">
        <ContactRow glyph={icon.phone} label="Phone" value={PROFILE.phone} href={`tel:${PROFILE.phone.replace(/\s+/g, "")}`} />
        <ContactRow glyph={icon.email} label="Email" value={PROFILE.email} href={`mailto:${PROFILE.email}`} />
        <ContactRow glyph={icon.web} label="Web" value={PROFILE.web.replace(/^https?:\/\//, "")} href={PROFILE.web} />
        <ContactRow glyph={icon.x} label="X" value={`@${PROFILE.x}`} href={`https://x.com/${PROFILE.x}`} />
        <ContactRow glyph={icon.linkedin} label="LinkedIn" value={`in/${PROFILE.linkedin}`} href={`https://www.linkedin.com/in/${PROFILE.linkedin}`} />
        <ContactRow glyph={icon.reddit} label="Reddit" value={`u/${PROFILE.reddit}`} href={`https://www.reddit.com/user/${PROFILE.reddit}`} />
      </div>

      <p className="mt-8 text-center text-[11px] text-kfaint font-mono">kurnal.ai</p>
    </div>
  </div>
);

export default ProfilePage;
